import React from "react";
import { Switch, Radio } from "@my-scoot/component-library-legacy";
import Tooltip from "ui/modules/Tooltip";
import {
  course_availability_options,
  SHARE_RECORDING_TEXTS,
  SHARE_RECORDING_DISPLAY_NAME,
} from "./ShareRecording.constant";
import styles from "./ShareRecording.module.css";
import { SectionLocked } from "features/AdminPanel/modules/SectionLocked/SectionLocked";
import { RecordingFaqModal } from "./RecordingFaqModal/RecordingFaqModal";
import UpgradeTooltip from "features/Account/modules/UpgradeTooltip/UpgradeTooltip";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import { useShareRecording } from "./hooks/useShareRecording";
import { checkShowRecordingAvailability } from "./ShareRecording.util";

/**
 * ShareRecording Component
 *
 * Renders the session recording controls for an offering page, including:
 * 1. "Session recording" toggle (mapped to `is_auto_recordable` form field)
 * 2. "Auto-package recordings into a course" toggle (mapped to `map_to_course` form field)
 * 3. Customer recording access control settings (mapped to `recording_accessibility`)
 * 4. FAQ Modal trigger and information banner
 *
 * @param {Object} props
 * @param {boolean} props.isAutoRecordable - Active is_auto_recordable state from parent form
 * @param {boolean} [props.mapToCourse] - Active map_to_course state from parent form
 * @param {string|number} props.recordingAccessControl - Customer recording access level
 * @param {boolean} props.isAccessControlEditable - Flag determining if access radio options can be modified
 * @param {number} props.addressType - Location address type enum value
 * @param {Object} props.form - React Final Form instance
 * @param {number} props.type - Offering type enum value
 */
const ShareRecording = React.forwardRef((props, ref) => {
  const {
    isAutoRecordable,
    mapToCourse,
    recordingAccessControl,
    isAccessControlEditable,
    addressType,
    form,
    type,
    showAutoPackage = false,
  } = props;

  // Custom hook handling menu permissions, computed switch states, and form handlers
  const {
    hasShareRecordingAccess,
    showFaqModal,
    setShowFaqModal,
    isSessionRecordingOn,
    isSessionRecordingDisabled,
    isAutoPackageDisabled,
    isAutoPackageChecked,
    currentAccessibility,
    sessionTitle,
    sessionDesc,
    handleSessionRecordingToggle,
    handleAutoPackageToggle,
    handleCustomerRecordingAccessChange,
  } = useShareRecording({
    isAutoRecordable,
    mapToCourse,
    recordingAccessControl,
    showAutoPackage,
    form,
  });

  React.useImperativeHandle(ref, () => ({
    getValues: () => ({
      is_auto_recordable: isSessionRecordingOn,
      map_to_course: isAutoPackageChecked,
      recording_accessibility:
        currentAccessibility || recordingAccessControl || "1",
    }),
  }));

  // Determines whether customer recording access options should be shown based on offering type & address type
  const showRecordingAvailability = checkShowRecordingAvailability({
    type,
    addressType,
  });

  return (
    <>
      {/* Information Banner: Shown when session recording toggle is ON */}
      {isSessionRecordingOn && showAutoPackage && (
        <div className="tw-flex tw-items-start tw-gap-2 tw-mt-3 tw-p-3 tw-bg-blue-50 tw-border tw-border-solid tw-border-blue-200 tw-rounded-lg tw-text-xs tw-text-blue-800">
          <InfoOutlinedIcon className="!tw-w-4 !tw-h-4 tw-text-blue-600 tw-shrink-0 tw-mt-0.5" />
          <span>{SHARE_RECORDING_TEXTS.FUTURE_RECORDINGS_BANNER}</span>
        </div>
      )}

      {/* Session recording section (is_auto_recordable) */}
      <div className="tw-pt-4">
        <div className="tw-flex tw-justify-between tw-items-start">
          <div className="tw-pr-4">
            <div className="tw-text-[16px] tw-font-medium tw-text-black mb-1">
              {sessionTitle}
            </div>
            <div className="tw-text-sm tw-text-black/40 tw-leading-relaxed">
              {sessionDesc}
            </div>
          </div>
          <div className="tw-flex tw-shrink-0">
            {!hasShareRecordingAccess && (
              <UpgradeTooltip
                displayTitle={SHARE_RECORDING_TEXTS.UPGRADE_TITLE}
              >
                <SectionLocked />
              </UpgradeTooltip>
            )}
            <Switch
              checked={isSessionRecordingOn}
              noIcon
              size="large"
              disabled={isSessionRecordingDisabled}
              onChange={handleSessionRecordingToggle}
            />
          </div>
        </div>
      </div>

      {/* Auto-package recordings into a course section (map_to_course) */}
      {showAutoPackage && (
        <div className="tw-mt-6 tw-pt-6 tw-border-t tw-border-b-0 tw-border-r-0 tw-border-l-0 tw-border-solid tw-border-gray-200">
          <div className="tw-flex tw-justify-between tw-items-start">
            <div className="tw-pr-4">
              <div className="tw-text-[16px] tw-font-medium tw-text-black mb-1">
                {SHARE_RECORDING_TEXTS.AUTO_PACKAGE_TITLE}
              </div>
              <div className="tw-text-sm tw-text-black/40 tw-leading-relaxed">
                {SHARE_RECORDING_TEXTS.AUTO_PACKAGE_DESC}
              </div>
            </div>
            <div className="tw-flex tw-shrink-0">
              <Switch
                checked={isAutoPackageChecked}
                noIcon
                size="large"
                disabled={isAutoPackageDisabled}
                onChange={handleAutoPackageToggle}
              />
            </div>
          </div>
        </div>
      )}

      {/* Customer recording availability section (recording_accessibility) */}
      {isSessionRecordingOn && showRecordingAvailability && (
        <div className="tw-mt-6 tw-pt-6 tw-border-solid tw-border-t tw-border-b-0 tw-border-r-0 tw-border-l-0 tw-border-gray-200">
          <div className={styles.share_recording_text_style}>
            {SHARE_RECORDING_TEXTS.CUSTOMER_ACCESS_QUESTION}
          </div>
          <div className={styles.radio_options_style}>
            {course_availability_options.map((option) => (
              <div key={option.value} className={styles.single_row_style}>
                <Radio
                  value={option.value}
                  checked={+currentAccessibility === +option.value}
                  onChange={(e) => handleCustomerRecordingAccessChange(e)}
                  disabled={!isAccessControlEditable}
                />
                <div className={styles.single_label_info_style}>
                  <div className={styles.label_style}>{option.label}</div>
                  <Tooltip text={option.tooltipText} />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* FAQ Information Banner & Modal */}
      <div className="tw-text-sm tw-bg-[#EFF6FF] tw-border-l-[2.5px] tw-border-t-0 tw-border-r-0 tw-border-b-0 tw-border-solid tw-border-[#4170C1] tw-rounded-md tw-px-3 tw-py-1 tw-mt-6">
        {SHARE_RECORDING_TEXTS.FAQ_PROMPT}&nbsp;
        <span
          className="tw-text-[#002CCD] tw-underline tw-cursor-pointer"
          onClick={() => setShowFaqModal(true)}
        >
          {SHARE_RECORDING_TEXTS.FAQ_LINK}
        </span>
      </div>
      <RecordingFaqModal
        open={showFaqModal}
        onClose={() => setShowFaqModal(false)}
        showLock={!hasShareRecordingAccess}
      />
    </>
  );
});

ShareRecording.displayName = SHARE_RECORDING_DISPLAY_NAME;

export default ShareRecording;
